import { create } from 'zustand';
import {
  ADMIN_USER_ROLES,
  type AdminLoginResponse,
  type AdminLoginSuccessResponse,
  type AdminMfaEnrollmentResponse,
  type AdminRefreshResponse,
  type AdminUserRole,
} from '@shared/types';
import {
  api,
  ApiError,
  setRefreshHandler,
  setTokenProvider,
  setUnauthorizedHandler,
} from '../services/api';

/**
 * Minimal admin-session user shape. Mirrors what the server's admin-login
 * endpoints return in their success payload. We deliberately keep only what
 * the layout/guards need.
 */
export interface AdminSessionUser {
  id: string;
  username: string;
  displayName: string;
  role: AdminUserRole;
  mfaEnrolled: boolean;
}

/**
 * In-memory state for a pending TOTP enrollment. The secret and recovery
 * codes are server-generated one-shot values — they MUST NEVER be persisted
 * to localStorage. If the user refreshes mid-enrollment the state is lost
 * and the flow restarts.
 */
export interface PendingEnrollment {
  otpauthUri: string;
  secret: string;
  recoveryCodes: string[];
}

/**
 * Finite state machine for the admin sign-in flow. In production every admin
 * uses MFA, so a successful password exchange transitions into one of the MFA
 * states. Non-prod servers may run with MFA disabled (ADMIN_MFA_ENABLED unset
 * in dev) — there the password exchange answers `authenticated` directly and
 * we jump straight to that state.
 *
 * `hydrating` is the one-shot bootstrap call to /admin-refresh on app load.
 * We sit in this state until the call resolves so the router can avoid a
 * "flash of login page" when the user actually has a valid refresh cookie.
 */
export type AuthStatus =
  | 'unauthenticated'
  | 'hydrating' // app boot — checking for a live refresh cookie
  | 'authenticating' // password POST in flight
  | 'mfa_challenge' // challenge token issued, awaiting TOTP or recovery code
  | 'mfa_enroll_pending' // first-login MFA: awaiting QR scan + confirm
  | 'authenticated'
  | 'error';

/**
 * We persist a minimal "remember-me hint" in localStorage so the SPA can
 * skip the hydrate flicker on app load when there's clearly no session to
 * recover. The actual auth credential lives in the HttpOnly refresh cookie
 * and is never accessible to JS — this is purely a UX optimization.
 */
export const SESSION_HINT_STORAGE_KEY = 'wc_admin_session_hint';

interface SessionHint {
  rememberMe: boolean;
  /** Last-known display info — for "Welcome back, X" hints only. NOT trusted. */
  displayName: string | null;
  username: string | null;
  /** Approximate refresh-cookie expiry (UNIX ms). Hint only. */
  refreshExpiresHintAt: number | null;
}

export interface AuthState {
  status: AuthStatus;
  /**
   * In-memory access token. Deliberately NOT persisted — XSS-grade attacks
   * that exfiltrate localStorage shouldn't yield a usable admin credential.
   * Persistence is delegated to the HttpOnly refresh cookie + /admin-refresh.
   */
  token: string | null;
  /** Approximate access-token expiry (UNIX ms) — used for proactive renewal. */
  accessTokenExpiresAt: number | null;
  adminUser: AdminSessionUser | null;
  /** Toggled by the user on the Login screen. Persists in `SessionHint`. */
  rememberMe: boolean;
  /**
   * Short-lived opaque challenge token from the server. In-memory only —
   * never persisted. Cleared on logout, error, or successful authentication.
   */
  mfaChallengeToken: string | null;
  /**
   * In-memory pending enrollment material. Never persisted. Cleared after
   * successful enrollment confirmation, on logout, or on flow reset.
   */
  enrollment: PendingEnrollment | null;
  error: string | null;

  setRememberMe: (v: boolean) => void;
  submitPassword: (credentials: {
    username: string;
    password: string;
  }) => Promise<void>;
  beginEnrollment: () => Promise<void>;
  confirmEnrollment: (code: string) => Promise<void>;
  submitMfaCode: (code: string) => Promise<void>;
  /**
   * Attempt to mint a fresh access token from the refresh cookie. Returns
   * the new access token on success, or null if the refresh failed (the
   * caller should treat null as "user must re-authenticate").
   */
  refresh: () => Promise<string | null>;
  /** Server-side logout. Always succeeds from the client's perspective. */
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
}

const ADMIN_USER_ROLE_SET = new Set<string>(ADMIN_USER_ROLES);

function isAdminUserRole(v: unknown): v is AdminUserRole {
  return typeof v === 'string' && ADMIN_USER_ROLE_SET.has(v);
}

function readSessionHint(): SessionHint {
  try {
    const raw = localStorage.getItem(SESSION_HINT_STORAGE_KEY);
    if (!raw) return emptyHint();
    const parsed = JSON.parse(raw) as Partial<SessionHint>;
    return {
      rememberMe: parsed.rememberMe === true,
      displayName: typeof parsed.displayName === 'string' ? parsed.displayName : null,
      username: typeof parsed.username === 'string' ? parsed.username : null,
      refreshExpiresHintAt:
        typeof parsed.refreshExpiresHintAt === 'number'
          ? parsed.refreshExpiresHintAt
          : null,
    };
  } catch {
    return emptyHint();
  }
}

function emptyHint(): SessionHint {
  return {
    rememberMe: false,
    displayName: null,
    username: null,
    refreshExpiresHintAt: null,
  };
}

function writeSessionHint(hint: SessionHint): void {
  try {
    localStorage.setItem(SESSION_HINT_STORAGE_KEY, JSON.stringify(hint));
  } catch {
    // Storage exception (private mode, full quota) — proceed without the hint.
  }
}

function clearSessionHint(): void {
  try {
    localStorage.removeItem(SESSION_HINT_STORAGE_KEY);
  } catch {
    // Best-effort cleanup; ignore.
  }
}

/**
 * Map a server `AdminLoginSuccessResponse` into the in-memory session shape,
 * or throw if the role tier is outside the known set (defense-in-depth
 * against a compromised/mis-configured server returning a bogus tier).
 */
function toSessionUser(res: AdminLoginSuccessResponse): AdminSessionUser {
  if (!isAdminUserRole(res.adminUser.role)) {
    throw new ApiError(
      403,
      'admin_access_denied',
      'This account does not have admin access',
    );
  }
  return {
    id: res.adminUser.id,
    username: res.adminUser.username,
    displayName: res.adminUser.displayName,
    role: res.adminUser.role,
    mfaEnrolled: res.adminUser.mfaEnrolled,
  };
}

function friendlyError(err: unknown, fallback = 'Request failed'): string {
  if (err instanceof ApiError) {
    switch (err.error) {
      case 'invalid_admin_credentials':
        return 'Invalid username or password.';
      case 'account_banned':
        return 'This account is banned.';
      case 'invalid_mfa_code':
      case 'invalid_mfa_token':
        return 'Invalid code. Please try again.';
      case 'mfa_challenge_expired':
        return 'This sign-in attempt expired. Please start over.';
      case 'admin_access_denied':
        return 'This account does not have admin access.';
      case 'admin_refresh_invalid':
      case 'admin_refresh_required':
        return 'Your session expired. Please sign in again.';
      case 'admin_refresh_reuse_detected':
        return 'A security issue was detected with your session. Please sign in again.';
      default:
        return err.message || fallback;
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Common headers required by the cookie-authenticated admin endpoints.
 * `X-Refresh-Request: 1` is the CSRF defense-in-depth header (server checks
 * it before accepting any cookie-only POST). `Content-Type` is set when the
 * call carries a JSON body — refresh + logout don't, but the header is
 * harmless on those.
 */
function refreshHeaders(): HeadersInit {
  return { 'X-Refresh-Request': '1' };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'hydrating',
  token: null,
  accessTokenExpiresAt: null,
  adminUser: null,
  rememberMe: readSessionHint().rememberMe,
  mfaChallengeToken: null,
  enrollment: null,
  error: null,

  setRememberMe(v) {
    set({ rememberMe: v });
  },

  async submitPassword({ username, password }) {
    set({ status: 'authenticating', error: null });
    const rememberMe = get().rememberMe;
    try {
      // `rememberMe` rides the password step because a server with MFA
      // disabled (non-prod, ADMIN_MFA_ENABLED unset) answers this call with
      // the authenticated session + refresh cookie directly. When MFA is on,
      // the server ignores it here and reads it from the MFA/confirm step.
      const res = await api<AdminLoginResponse>('/auth/admin-login', {
        method: 'POST',
        body: JSON.stringify({ username, password, rememberMe }),
        skipAuth: true,
      });

      if (res.kind === 'authenticated') {
        // Direct session — the server runs with MFA disabled (non-prod
        // only; prod always requires MFA and answers `mfa_required`).
        const sessionUser = toSessionUser(res);
        commitSession(set, res, sessionUser);
        return;
      }

      if (res.kind === 'mfa_required') {
        set({
          status: res.enrollmentRequired
            ? 'mfa_enroll_pending'
            : 'mfa_challenge',
          mfaChallengeToken: res.mfaChallengeToken,
          enrollment: null,
          error: null,
        });
        return;
      }

      throw new Error('Malformed admin-login response');
    } catch (err) {
      set({
        status: 'error',
        mfaChallengeToken: null,
        enrollment: null,
        error: friendlyError(err, 'Login failed'),
      });
      throw err;
    }
  },

  async beginEnrollment() {
    const challenge = get().mfaChallengeToken;
    if (!challenge) {
      const msg = 'Missing enrollment challenge — please sign in again.';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    try {
      const res = await api<AdminMfaEnrollmentResponse>(
        '/auth/admin-mfa/enroll',
        {
          method: 'POST',
          body: JSON.stringify({ mfaChallengeToken: challenge }),
          skipAuth: true,
        },
      );
      set({
        status: 'mfa_enroll_pending',
        enrollment: {
          otpauthUri: res.otpauthUri,
          secret: res.secret,
          recoveryCodes: res.recoveryCodes,
        },
        error: null,
      });
    } catch (err) {
      set({
        error: friendlyError(err, 'Failed to start MFA enrollment'),
      });
      throw err;
    }
  },

  async confirmEnrollment(code) {
    const challenge = get().mfaChallengeToken;
    if (!challenge) {
      const msg = 'Missing enrollment challenge — please sign in again.';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    const rememberMe = get().rememberMe;
    try {
      const res = await api<AdminLoginSuccessResponse>(
        '/auth/admin-mfa/confirm',
        {
          method: 'POST',
          body: JSON.stringify({
            mfaChallengeToken: challenge,
            code,
            rememberMe,
          }),
          skipAuth: true,
        },
      );
      const sessionUser = toSessionUser(res);
      commitSession(set, res, sessionUser);
    } catch (err) {
      // Keep status=mfa_enroll_pending so the user can re-enter the code.
      // Keep mfaChallengeToken + enrollment intact for retry.
      set({
        error: friendlyError(err, 'Could not confirm enrollment'),
      });
      throw err;
    }
  },

  async submitMfaCode(code) {
    const challenge = get().mfaChallengeToken;
    if (!challenge) {
      const msg = 'Missing MFA challenge — please sign in again.';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    const rememberMe = get().rememberMe;
    try {
      const res = await api<AdminLoginSuccessResponse>(
        '/auth/admin-login/mfa',
        {
          method: 'POST',
          body: JSON.stringify({
            mfaChallengeToken: challenge,
            code,
            rememberMe,
          }),
          skipAuth: true,
        },
      );
      const sessionUser = toSessionUser(res);
      commitSession(set, res, sessionUser);
    } catch (err) {
      // Stay in mfa_challenge so the user can retry — do NOT clear the
      // challenge token, or they'd have to restart the whole flow.
      set({
        error: friendlyError(err, 'Could not verify code'),
      });
      throw err;
    }
  },

  async refresh() {
    try {
      const res = await api<AdminRefreshResponse>('/auth/admin-refresh', {
        method: 'POST',
        skipAuth: true,
        headers: refreshHeaders(),
      });
      const sessionUser = toSessionUser(res);
      commitSession(set, res, sessionUser);
      return res.token;
    } catch (err) {
      // Refresh failure means: no cookie, expired cookie, reuse detected,
      // or revocation. In all cases the user must re-authenticate. We
      // clear in-memory state but keep the localStorage hint so the next
      // load knows whether to even attempt /admin-refresh on boot.
      const wasAuthenticated = get().status === 'authenticated';
      set({
        status: wasAuthenticated ? 'unauthenticated' : get().status,
        token: null,
        accessTokenExpiresAt: null,
        adminUser: wasAuthenticated ? null : get().adminUser,
        error: friendlyError(err, 'Session expired'),
      });
      return null;
    }
  },

  async logout() {
    // Tell the server first so the refresh cookie is cleared and the
    // session record is tombstoned. We swallow errors — the client must
    // be able to log out even if the server is unreachable.
    try {
      await api('/auth/admin-logout', {
        method: 'POST',
        skipAuth: true,
        headers: refreshHeaders(),
      });
    } catch {
      // Network failure shouldn't trap the user in an authed state.
    }
    clearSessionHint();
    set({
      status: 'unauthenticated',
      token: null,
      accessTokenExpiresAt: null,
      adminUser: null,
      rememberMe: false,
      mfaChallengeToken: null,
      enrollment: null,
      error: null,
    });
  },

  async hydrate() {
    const hint = readSessionHint();
    // Skip the refresh probe if we have no hint AND we're certain there's
    // no cookie. We can't actually inspect HttpOnly cookies from JS, so
    // the heuristic is: if the user never opted into Remember Me AND they
    // have no fresh-session hint (rememberMe=false hint with a recent
    // expiresHintAt), don't bother with a network round-trip on every
    // page load. The refresh probe is harmless if the cookie is absent
    // (returns 401 fast), so erring on "always probe" is also fine — we
    // just skip the spinner flicker for users who deliberately logged out.
    const shouldProbe =
      hint.rememberMe ||
      (hint.refreshExpiresHintAt !== null && hint.refreshExpiresHintAt > Date.now());
    if (!shouldProbe) {
      set({ status: 'unauthenticated' });
      return;
    }
    set({ status: 'hydrating' });
    await get().refresh();
    if (get().status === 'hydrating') {
      // refresh() didn't transition us — must have failed silently.
      set({ status: 'unauthenticated' });
    }
  },
}));

/**
 * Commit a successful login / refresh / enroll-confirm response into the
 * store. Centralized so all three call sites stay symmetric — drift here
 * would manifest as missing-token bugs that only surface after refresh.
 */
function commitSession(
  set: (
    partial:
      | Partial<AuthState>
      | ((s: AuthState) => Partial<AuthState>),
  ) => void,
  res: AdminLoginSuccessResponse,
  sessionUser: AdminSessionUser,
): void {
  // Persist a hint so the next page load knows whether to probe /refresh.
  // The actual credential lives in the HttpOnly cookie, NOT here.
  const hintRefreshTtlMs = res.rememberMe
    ? 30 * 24 * 60 * 60 * 1000 // matches REFRESH_TTL_LONG on the server
    : 24 * 60 * 60 * 1000; // matches REFRESH_TTL_SHORT
  writeSessionHint({
    rememberMe: res.rememberMe,
    displayName: sessionUser.displayName,
    username: sessionUser.username,
    refreshExpiresHintAt: Date.now() + hintRefreshTtlMs,
  });
  set({
    status: 'authenticated',
    token: res.token,
    accessTokenExpiresAt: res.accessTokenExpiresAt,
    adminUser: sessionUser,
    rememberMe: res.rememberMe,
    mfaChallengeToken: null,
    enrollment: null,
    error: null,
  });
}

/**
 * Selector for the api client — avoids importing the zustand hook from
 * non-React modules.
 */
export function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

// Wire the api client -> auth store token + refresh + logout-on-401.
setTokenProvider(() => useAuthStore.getState().token);
setRefreshHandler(() => useAuthStore.getState().refresh());
setUnauthorizedHandler(() => {
  // Refresh handler already attempted recovery before we get here; this
  // fires when refresh has also failed. Fire-and-forget the server logout
  // so the cookie is cleared, but don't await — the caller is mid-401.
  void useAuthStore.getState().logout();
});
